// src/hr/portal/PeoplePage.js -- HR's home (mockup screen 2): KPI tiles, search + filter chips and the
// people table with live balances, account pills and flags. "Log time off" opens the side drawer;
// "Add person" opens the add drawer and then the new person's profile.
// The list comes from the app (GET /admin/people?status=all, loaded once and shared with the menu);
// the chips filter it here. Picking a manager asks the server for their direct reports
// (?managerId=), and the chips and search then filter that list. No notes or reasons ever appear here.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiAlertTriangle, FiArrowRight, FiPlus, FiRefreshCw, FiSearch, FiUserPlus, FiUsers } from 'react-icons/fi';
import { hr } from '../api';
import { Avatar, Btn, Card, EmptyState, Field, Pill, Skeleton } from '../kit';
import { daysLabel, daysNumber } from '../format';
import LogTimeOffDrawer from './LogTimeOffDrawer';
import AddPersonDialog from './AddPersonDialog';
import { LOW_PTO_HOURS, accountPill, fullName, isCurrent, lifecyclePill } from './portalUtils';

const ptoOf = (p) => (p.balances || []).find((b) => b.key === 'pto') || null;
const sickOf = (p) => (p.balances || []).find((b) => b.key === 'sick') || null;
const isLowPto = (p) => {
  const pto = ptoOf(p);
  return !!pto && pto.totalHours > 0 && pto.availableHours < LOW_PTO_HOURS;
};

const FILTERS = [
  { key: 'all', label: 'All', test: isCurrent },
  { key: 'unsigned', label: 'Not signed in yet', test: (p) => isCurrent(p) && p.accountStatus === 'none' },
  { key: 'low', label: 'Low PTO', sr: '(less than 2 days left)', test: (p) => isCurrent(p) && isLowPto(p) },
  { key: 'leaving', label: 'Leaving', test: (p) => !p.archived && p.status === 'leaving' },
  { key: 'left', label: 'Left', test: (p) => !p.archived && p.status === 'left' },
  { key: 'archived', label: 'Archived', test: (p) => !!p.archived }
];

function matches(p, query) {
  if (!query) return true;
  const hay = [fullName(p), p.email, ...(p.emails || []), p.department, p.title].filter(Boolean).join(' ').toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((word) => hay.includes(word));
}

function MiniBar({ balance, kind, name }) {
  if (!balance || !(balance.totalHours > 0)) return <span className="hrp-pp-bal__none">Not set up</span>;
  const available = Number(balance.availableHours) || 0;
  const total = Number(balance.totalHours) || 0;
  const share = Math.max(0, Math.min(1, available / total));
  const low = kind === 'pto' && available < LOW_PTO_HOURS;
  return (
    <div className="hrp-pp-bal" data-kind={kind} data-low={low ? 'true' : undefined}>
      <div className="hrp-pp-bal__t" aria-hidden="true">
        <b>{daysNumber(available)}</b>
        <small>{` / ${daysNumber(total)}`}</small>
      </div>
      <span className="hrp-sr">{`${name}: ${daysLabel(available)} of ${daysLabel(total)} left`}</span>
      <div className="hrp-pp-bal__track" aria-hidden="true">
        <span style={{ width: `${Math.round(share * 100)}%` }} />
      </div>
    </div>
  );
}

function Kpis({ kpis }) {
  const k = kpis || {};
  const tiles = [
    { label: 'Active people', value: k.active, sub: k.newThisMonth ? `${k.newThisMonth} new ${k.newThisMonth === 1 ? 'hire' : 'hires'} this month` : 'No new hires this month' },
    { label: 'Out today', value: k.outToday, sub: `${k.outThisWeek || 0} out this week` },
    {
      label: 'Unsigned policies',
      value: k.unsignedPeople,
      unit: k.unsignedPeople === 1 ? 'person' : 'people',
      sub: k.unsignedPeople ? <Link to="policies" className="hrp-pp-kpi__link">See who in Policies</Link> : 'Everyone is signed up'
    },
    {
      label: 'Expiring soon',
      value: k.expiringSoon,
      sub: k.expiringSoon ? <Link to="reminders" className="hrp-pp-kpi__link">Within 60 days · Reminders</Link> : 'Nothing in the next 60 days',
      warn: k.expiringSoon > 0
    }
  ];
  return (
    <div className="hrp-pp-kpis">
      {tiles.map((t, i) => (
        <Card key={t.label} delay={i} className="hrp-pp-kpi" data-warn={t.warn ? 'true' : undefined}>
          <div className="hrp-lab">{t.label}</div>
          <div className="hrp-pp-kpi__v">
            {t.value == null ? '—' : t.value}
            {t.unit && <small>{t.unit}</small>}
          </div>
          <div className="hrp-pp-kpi__s">{t.sub}</div>
        </Card>
      ))}
    </div>
  );
}

// Last year's close-out still to run (R54, from the people list KPIs): until it runs, unused Sick &
// Safe hours from that year are neither carried over nor paid out.
function YearEndBanner({ due }) {
  if (!due || !due.fromYear) return null;
  const title = `Run the ${due.fromYear} year-end close-out`;
  return (
    <div className="hrp-banner hrp-pp-ye" role="note" aria-labelledby="hrp-pp-ye-title">
      <span className="hrp-banner__ic" aria-hidden="true"><FiAlertTriangle /></span>
      <div className="hrp-banner__txt">
        <div className="hrp-banner__title" id="hrp-pp-ye-title">{title}</div>
        <div className="hrp-banner__text">
          {`Until it runs, unused Sick & Safe hours from ${due.fromYear} aren't carried over or paid out, and ${due.fromYear + 1} allowances aren't set from it.`}
        </div>
      </div>
      <Link to={`year-end?year=${due.fromYear}`} className="hrp-btn hrp-btn--main hrp-btn--sm hrp-pp-ye__go">
        <span className="hrp-btn__label">{`Open the ${due.fromYear} close-out`}</span>
        <span className="hrp-btn__ic" aria-hidden="true"><FiArrowRight /></span>
      </Link>
    </div>
  );
}

function PersonRow({ p, selected, onLog, onOpen }) {
  const name = fullName(p);
  const account = accountPill(p.accountStatus);
  const life = lifecyclePill(p);
  const open = (e) => {
    if (e.target.closest('a, button, input, select, textarea')) return;
    onOpen(p);
  };
  return (
    <tr data-sel={selected ? 'true' : undefined} onClick={open}>
      <td data-label="Person" className="hrp-pp-td-person">
        <div className="hrp-pp-nm">
          <Avatar id={p.id} name={name} size="md" />
          <div className="hrp-pp-nm__txt">
            <Link to={`people/${p.id}`} className="hrp-pp-nm__link">{name}</Link>
            <span>{p.email}</span>
          </div>
        </div>
      </td>
      <td data-label="Department" className="hrp-pp-td-dept">{p.department || <span className="hrp-dim">—</span>}</td>
      <td data-label="PTO" className="hrp-pp-td-bal"><MiniBar balance={ptoOf(p)} kind="pto" name="PTO" /></td>
      <td data-label="Sick & Safe" className="hrp-pp-td-bal"><MiniBar balance={sickOf(p)} kind="sick" name="Sick & Safe" /></td>
      <td data-label="Account" className="hrp-pp-td-pills">
        <div className="hrp-pp-pills">
          <Pill tone={account.tone} dot>{account.label}</Pill>
          {life && <Pill tone={life.tone}>{life.label}</Pill>}
          {p.outToday && isCurrent(p) && <Pill tone="mute">Out today</Pill>}
          {p.flags && p.flags.sickRun && <Pill tone="bad">3+ sick days in a row</Pill>}
          {p.flags && p.flags.unsignedPolicies > 0 && (
            <Pill tone="warn">{`${p.flags.unsignedPolicies} ${p.flags.unsignedPolicies === 1 ? 'policy' : 'policies'} to sign`}</Pill>
          )}
        </div>
      </td>
      <td className="hrp-pp-td-act">
        {!p.archived && (
          <button type="button" className="hrp-link hrp-pp-act" aria-label={`Log time off for ${name}`} onClick={() => onLog(p)}>
            <FiPlus aria-hidden="true" /> Log time off
          </button>
        )}
      </td>
    </tr>
  );
}

// The chosen manager's direct reports, from GET /admin/people?status=all&managerId=. Reloads when the
// shared list does (after a log or an edit) so balances stay current; late answers are dropped.
function useManagerPeople(managerId, shared) {
  const [state, setState] = useState({ managerId: '', status: 'idle', people: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!managerId) return undefined;
    let live = true;
    setState((s) => (s.managerId === managerId ? { ...s, status: 'loading' } : { managerId, status: 'loading', people: null }));
    Promise.resolve()
      .then(() => hr.admin.people.list({ status: 'all', managerId }))
      .then((body) => {
        if (live) setState({ managerId, status: 'ready', people: (body && body.people) || [] });
      })
      .catch(() => {
        if (live) setState((s) => ({ ...s, managerId, status: 'error' }));
      });
    return () => { live = false; };
  }, [managerId, shared, attempt]);

  const current = managerId && state.managerId === managerId ? state : { managerId, status: managerId ? 'loading' : 'idle', people: null };
  return { ...current, retry: () => setAttempt((n) => n + 1) };
}

function LoadingPeople() {
  return (
    <>
      <div className="hrp-pp-kpis">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="block" height={96} />)}
      </div>
      <Card className="hrp-pp-card">
        <Skeleton label="Loading people" lines={6} height={16} />
      </Card>
    </>
  );
}

export function PeoplePage({ today, people, types, holidays, context, yearRange, onToast }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [logFor, setLogFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [managerId, setManagerId] = useState('');

  const everyone = useMemo(() => (people.data && people.data.people) || [], [people.data]);
  const kpis = people.data && people.data.kpis;
  // People who manage at least one active person (sent by the server with the list).
  const managers = useMemo(() => (people.data && Array.isArray(people.data.managers) ? people.data.managers : []), [people.data]);
  const team = useManagerPeople(managerId, people.data);
  const teamWaiting = !!managerId && !team.people;
  const all = useMemo(() => (managerId ? team.people || [] : everyone), [managerId, team.people, everyone]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, all.filter(f.test).length])), [all]);
  const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const shown = all.filter(active.test).filter((p) => matches(p, query.trim()));
  const managerOptions = [{ value: '', label: 'Any manager' }, ...managers.map((m) => ({ value: m.id, label: fullName(m) }))];
  if (managerId && !managers.some((m) => m.id === managerId)) managerOptions.push({ value: managerId, label: 'Former manager' });
  const managerName = managerId ? (managerOptions.find((o) => o.value === managerId) || {}).label : '';
  const showEveryone = () => { setQuery(''); setFilter('all'); setManagerId(''); };

  const subtitle = kpis
    ? `${kpis.active} active · ${kpis.outToday} out today`
    : 'Everyone at a glance, with live balances';

  const logged = ({ text }) => {
    setLogFor(null);
    onToast(text);
    people.reload();
  };
  const added = (person, name) => {
    setAdding(false);
    people.reload();
    onToast(`${name} added. Set up their balances next.`);
    if (person && person.id) navigate(`people/${person.id}`);
  };

  let body;
  if (!people.data && people.status === 'error') {
    body = (
      <Card>
        <EmptyState icon={FiAlertCircle} title="Couldn't load people" action={<Btn variant="ghost" onClick={people.reload}>Try again</Btn>}>
          Something went wrong on our side. Try again in a moment.
        </EmptyState>
      </Card>
    );
  } else if (!people.data) {
    body = <LoadingPeople />;
  } else if (everyone.length === 0) {
    body = (
      <Card>
        <EmptyState icon={FiUsers} title="Add your first person" action={<Btn icon={FiUserPlus} onClick={() => setAdding(true)}>Add person</Btn>}>
          Add people by name and work email. They don't need to have signed in — you can set up their balances right away.
        </EmptyState>
      </Card>
    );
  } else {
    let emptyTitle = `No one in ${active.label}`;
    if (query.trim()) emptyTitle = `No one matches “${query.trim()}”`;
    else if (managerId) emptyTitle = filter === 'all' ? `No one reports to ${managerName}` : `No one in ${active.label} reports to ${managerName}`;
    body = (
      <>
        <Kpis kpis={kpis} />
        <div className="hrp-pp-bar">
          <Field
            label="Search people"
            hideLabel
            type="search"
            icon={<FiSearch />}
            placeholder="Search name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="hrp-pp-search"
          />
          {managers.length > 0 && (
            <Field
              label="Manager"
              hideLabel
              type="select"
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              options={managerOptions}
              className="hrp-pp-mgr"
            />
          )}
          <div className="hrp-chips" role="group" aria-label="Show">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" className="hrp-chip" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                {f.label}
                {f.sr && <span className="hrp-sr">{` ${f.sr}`}</span>}
                {' '}
                <span className="hrp-pp-chipn">{teamWaiting ? '–' : counts[f.key]}</span>
              </button>
            ))}
          </div>
        </div>
        {teamWaiting && team.status === 'error' && (
          <Card className="hrp-pp-card">
            <EmptyState compact icon={FiAlertCircle} title={`Couldn't load who reports to ${managerName}`} action={<Btn variant="ghost" size="sm" onClick={team.retry}>Try again</Btn>}>
              Something went wrong on our side. Try again in a moment.
            </EmptyState>
          </Card>
        )}
        {teamWaiting && team.status !== 'error' && (
          <Card className="hrp-pp-card">
            <Skeleton label={`Loading who reports to ${managerName}`} lines={4} height={16} />
          </Card>
        )}
        {!teamWaiting && <Card delay={4} className="hrp-pp-card">
          <div className="hrp-table-wrap">
            <table className="hrp-table hrp-stack-table hrp-pp-table">
              <caption className="hrp-sr">People</caption>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col" className="hrp-pp-td-dept">Department</th>
                  <th scope="col">PTO</th>
                  <th scope="col">Sick &amp; Safe</th>
                  <th scope="col">Account</th>
                  <th scope="col"><span className="hrp-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <PersonRow
                    key={p.id}
                    p={p}
                    selected={!!logFor && logFor.id === p.id}
                    onLog={setLogFor}
                    onOpen={(row) => navigate(`people/${row.id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {shown.length === 0 && (
            <EmptyState
              compact
              icon={FiSearch}
              title={emptyTitle}
              action={(query || filter !== 'all' || managerId) && (
                <Btn variant="ghost" size="sm" onClick={showEveryone}>Show everyone</Btn>
              )}
            >
              {query.trim() ? 'Try a first name, last name or email.' : 'Nobody fits this filter right now.'}
            </EmptyState>
          )}
        </Card>}
      </>
    );
  }

  return (
    <div className="hrp-page hrp-pp">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">People</h1>
          <p className="hrp-sub">{subtitle}</p>
        </div>
        <div className="hrp-page-head__acts">
          <Link to="year-end" className="hrp-btn hrp-btn--ghost hrp-btn--md">
            <span className="hrp-btn__ic" aria-hidden="true"><FiRefreshCw /></span>
            <span className="hrp-btn__label">Year-end close-out</span>
          </Link>
          <Btn icon={FiPlus} onClick={() => setAdding(true)}>Add person</Btn>
        </div>
      </div>

      <YearEndBanner due={kpis && kpis.yearEndDue} />
      {body}

      <LogTimeOffDrawer
        open={!!logFor}
        person={logFor}
        types={types}
        holidays={holidays}
        yearRange={yearRange}
        today={today}
        context={context}
        onClose={() => setLogFor(null)}
        onDone={logged}
      />
      <AddPersonDialog open={adding} people={everyone} onClose={() => setAdding(false)} onDone={added} />
    </div>
  );
}

export default PeoplePage;
